import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

/**
 * The app's one collapse behaviour, extracted from SidebarDesktop (KAN-2554).
 *
 * It was previously welded into the left route-menu sidebar: the expanded
 * boolean, the chevron toggle and the `k-sidebar-container active` class all
 * lived there and rendered a menu, not arbitrary content. Maria asked the Home
 * appointments rail to "use the same one we already have for the left-side
 * panel", so the shell moved here and both sides use it — one collapse
 * behaviour in the app, mirrored rather than copied.
 *
 * Controlled on purpose: Home has to widen the worklist column when the rail
 * collapses, so the parent owns the state.
 */
export interface CollapsiblePanelProps {
	/** Which edge the panel is docked to; decides which way the chevron points. */
	side: "left" | "right";
	expanded: boolean;
	onToggle: (expanded: boolean) => void;
	/** Container class; defaults to the existing left-sidebar class. */
	className?: string;
	/** i18n key for the toggle's accessible name. */
	toggleLabelKey?: string;
	testId?: string;
	children: React.ReactNode;
}

const CollapsiblePanel = (props: CollapsiblePanelProps) => {
	const { t } = useTranslation("common");

	const toggleLabelKey =
		props.toggleLabelKey ??
		(props.expanded
			? "UI_COMPONENTS.COLLAPSIBLE_PANEL.COLLAPSE"
			: "UI_COMPONENTS.COLLAPSIBLE_PANEL.EXPAND");

	const containerClass = `${props.className ?? "k-sidebar-container"}${props.expanded ? " active" : ""}`;

	// The chevron always points the way the panel is about to move, which is
	// why the two sides are mirror images rather than the same icon.
	const Chevron =
		props.side === "left"
			? props.expanded
				? FaChevronLeft
				: FaChevronRight
			: props.expanded
				? FaChevronRight
				: FaChevronLeft;

	return (
		<aside className={containerClass}>
			<button
				type="button"
				className="sidebar-toggle"
				aria-expanded={props.expanded}
				aria-label={t(toggleLabelKey)}
				data-testid={props.testId ? `${props.testId}-toggle` : undefined}
				onClick={() => props.onToggle(!props.expanded)}
			>
				<Chevron />
			</button>
			{props.children}
		</aside>
	);
};

export default CollapsiblePanel;
